'use client';

import { PoiCGType } from '@/types/graphql/root';
import ImageWithFallback from '../common/image-with-fallback';
import { filterNullUndefined, getPoiType } from '@/utils';
import Link from 'next/link';
import SpinnerLoader1818 from '@svg/common/spinner_loader_18_18.svg';
import HeartActive2422 from '@svg/main/poi-card/heart_active_24_22.svg';
import HeartDefault2422 from '@svg/main/poi-card/heart_default_24_22.svg';
import { useCallback, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { centerToastModalAtom } from '@/atom/modal';
import { togglePoiSaved } from '@/utils/actions';
import useToggleSavedQueryCache from '@/app/hooks/useToggleSavedQueryCache';

export default function SavedPoiCard({ poi }: { poi: PoiCGType }) {
  const { toggleSaved } = useToggleSavedQueryCache();

  const [isSaved, setIsSaved] = useState(poi.saved);
  const [loadingToggleSaved, setLoadingToggleSaved] = useState(false);

  const setCenterToastModal = useSetAtom(centerToastModalAtom);

  const onClickToggleSave = useCallback(async () => {
    try {
      setLoadingToggleSaved(true);
      const togglePoiSaveResponse = await togglePoiSaved(poi.id);
      if (togglePoiSaveResponse.status === 200) {
        setIsSaved(togglePoiSaveResponse.togglePoiSave?.saved ?? false);
        toggleSaved(poi);

        document.dispatchEvent(
          new CustomEvent(`poi:saved:${poi.id}`, {
            detail: {
              saved: togglePoiSaveResponse.togglePoiSave?.saved ?? false,
            },
          }),
        );
      } else {
        console.error(togglePoiSaveResponse);
        setCenterToastModal({
          renderingTime: 1500,
          text: '로그인이\n필요 합니다.',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingToggleSaved(false);
    }
  }, []);

  useEffect(() => {
    const poiSavedEventHandler = (event: any) => {
      if (typeof event.detail.saved === 'boolean') {
        setIsSaved(event.detail.saved);
      }
    };

    document.addEventListener(`poi:saved:${poi.id}`, poiSavedEventHandler);

    return () => {
      document.removeEventListener(`poi:saved:${poi.id}`, poiSavedEventHandler);
    };
  }, []);

  return (
    <Link
      href={`/poi/${poi.id}?from=main`}
      scroll={false}
      prefetch={false}
      className='w-[calc((100%-28px)/3)] relative overflow-hidden hover-upside'
    >
      <div className='relative'>
        <ImageWithFallback
          fallbackSrc='https://hoolzzuk-public-assets.s3.ap-northeast-2.amazonaws.com/custom-poi/custom_attraction_160.png'
          alt={poi.name}
          src={filterNullUndefined(poi.images)[0]?.imageUrl || ''}
          width={320}
          height={320}
          className='w-full aspect-square object-cover rounded-6pxr'
        />
        <div
          className='absolute bottom-0 w-full pb-4pxr pr-4pxr pl-6pxr pt-20pxr flex items-center justify-between rounded-6pxr'
          style={{
            backgroundImage: 'linear-gradient(180deg,rgba(51,51,51,0) 0%,rgba(51,51,51,.5) 100%)',
          }}
        >
          <p className='text-white truncate sub-heading-14-m'>{poi.city.name}</p>
          {loadingToggleSaved ? (
            <span className='w-24pxr h-22pxr px-3pxr py-2pxr'>
              <SpinnerLoader1818 className='animate-spin' />
            </span>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                onClickToggleSave();
              }}
            >
              {isSaved ? <HeartActive2422 /> : <HeartDefault2422 />}
            </button>
          )}
        </div>
      </div>
      <p className='text-gray-800 break-all mt-4pxr line-clamp-2 sub-heading-14-m max-h-37pxr mb-5pxr'>{poi.name}</p>
      <p className='text-gray-600 body-14-r'>{getPoiType(poi)}</p>
      {!isSaved && (
        <div
          className='absolute top-0 w-full aspect-square bg-black bg-opacity-25 rounded-6pxr backdrop-blur-sm flex items-center justify-center'
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          {loadingToggleSaved ? (
            <SpinnerLoader1818 className='animate-spin text-white' />
          ) : (
            <button
              className='bg-primary rounded-8pxr text-center text-white button-14-m px-10pxr py-5pxr'
              onClick={() => {
                if (loadingToggleSaved) return;
                onClickToggleSave();
              }}
            >
              다시 저장
            </button>
          )}
        </div>
      )}
    </Link>
  );
}
